<template>
  <tr class="even:bg-white odd:bg-gray-50">
    <td class="px-4 py-4 text-xs text-gray-900 align-top">
      <a role="button" class="underline" @click.prevent="showFacebookCampaign">
        <h3 class="text-xs font-normal">{{ facebookCampaign.name }}</h3>
      </a>
      <span class="inline-block mt-1 font-mono tabular-nums text-xs text-gray-500">{{
        facebookCampaign.id
      }}</span>
    </td>
    <td class="px-4 py-4 text-xs text-gray-900 tabular-nums text-right align-top">
      <BaseBadge
        :green="facebookCampaign.status === 'ACTIVE'"
        :silver="facebookCampaign.status !== 'ACTIVE'"
      >
        {{ facebookCampaign.status }}
      </BaseBadge>
    </td>
    <td class="px-4 py-4 text-xs text-gray-900 tabular-nums text-right align-top">
      {{ impressions }}
      <p v-if="$isAdminOrSuperAdmin">{{ costPerMille }}</p>
    </td>
    <td class="px-4 py-4 text-xs text-gray-900 tabular-nums text-right align-top">
      {{ reach }}
      <p v-if="$isAdminOrSuperAdmin">{{ costPerReach }}</p>
    </td>
    <td class="px-4 py-4 text-xs text-gray-900 tabular-nums text-right align-top">
      {{ pageviews }}
      <p v-if="$isAdminOrSuperAdmin" class="whitespace-nowrap">
        {{ pageviewsPerImpression }}% / {{ costPerPageView }}
      </p>
    </td>
    <td class="px-4 py-4 text-xs text-gray-900 tabular-nums text-right align-top">
      {{ contactRequestsCount }}
      <p v-if="$isAdminOrSuperAdmin">{{ spendAmountPerContactRequest }}</p>
    </td>
    <td
      v-if="$isAdminOrSuperAdmin"
      class="px-4 py-4 text-xs text-gray-900 tabular-nums text-right align-top"
    >
      {{ spendAmount }}
    </td>
    <td class="px-4 py-4 text-xs text-right text-gray-900 align-top">
      <span class="whitespace-nowrap">
        {{ createdAtShort }}
      </span>
      <p class="whitespace-nowrap">
        {{ createdAtFromNow }}
      </p>
    </td>
    <td v-if="$isAdminOrSuperAdmin" class="px-4 py-4 text-xs text-right text-gray-900 align-top">
      <RemoveFacebookCampaignAction
        :project-id="projectId"
        :facebook-campaign-id="facebookCampaign.id"
        @removed="$emit('removed')"
      />
    </td>
  </tr>
</template>
<script>
import numbro from '@/utils/numbro';
import moment from 'moment';
import RemoveFacebookCampaignAction from './RemoveFacebookCampaignAction.vue';

export default {
  components: {
    RemoveFacebookCampaignAction,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
    projectId: {
      type: String,
      required: true,
    },
    facebookCampaign: {
      type: Object,
      required: true,
    },
    facebookCampaignInsights: {
      type: Object,
      default: () => ({
        spend: 0,
        impressions: 0,
        reach: 0,
        clicks: 0,
        inline_link_clicks: 0,
        account_currency: 'EUR',
        contact_requests_count: 0,
      }),
    },
  },
  computed: {
    impressions() {
      return numbro(this.facebookCampaignInsights.impressions).format({
        thousandSeparated: true,
      });
    },
    costPerMille() {
      const spend = this.facebookCampaignInsights?.spend || 0;
      const impressions = this.facebookCampaignInsights?.impressions || 0;
      const spendPerImpression = impressions ? 1000 * (spend / impressions) : 0;

      return numbro(spendPerImpression / 100).formatCurrency({
        thousandSeparated: true,
        mantissa: 2,
      });
    },
    reach() {
      return numbro(this.facebookCampaignInsights.reach).format({
        thousandSeparated: true,
      });
    },
    costPerReach() {
      const spend = this.facebookCampaignInsights?.spend || 0;
      const reach = this.facebookCampaignInsights?.reach || 0;
      const spendPerReach = reach ? 1000 * (spend / reach) : 0;

      return numbro(spendPerReach / 100).formatCurrency({
        thousandSeparated: true,
        mantissa: 2,
      });
    },
    clicks() {
      return numbro(this.facebookCampaignInsights.clicks).format({
        thousandSeparated: true,
      });
    },
    pageviews() {
      return numbro(this.facebookCampaignInsights.inline_link_clicks).format({
        thousandSeparated: true,
      });
    },
    pageviewsPerImpression() {
      const inlineLinkClicks = this.facebookCampaignInsights?.inline_link_clicks || 0;
      const impressions = this.facebookCampaignInsights?.impressions || 0;
      if (!impressions) return 0;
      return numbro((inlineLinkClicks / impressions) * 100).format({
        thousandSeparated: true,
        mantissa: 2,
      });
    },
    costPerPageView() {
      const inlineLinkClicks = this.facebookCampaignInsights?.inline_link_clicks || 0;
      const spend = this.facebookCampaignInsights?.spend || 0;
      if (!inlineLinkClicks) return 0;
      return numbro(!inlineLinkClicks ? spend / 100 / inlineLinkClicks : 0).formatCurrency({
        thousandSeparated: true,
        mantissa: 2,
      });
    },
    contactRequestsCount() {
      return numbro(this.facebookCampaignInsights.contact_requests_count).format({
        thousandSeparated: true,
      });
    },
    spendAmount() {
      return numbro((this.facebookCampaignInsights?.spend || 0) / 100).formatCurrency({
        thousandSeparated: true,
        mantissa: 2,
      });
    },
    spendAmountPerContactRequest() {
      const spendPerContactRequest = this.facebookCampaignInsights.contact_requests_count
        ? (this.facebookCampaignInsights?.spend || 0) /
          this.facebookCampaignInsights.contact_requests_count
        : 0;

      return numbro(spendPerContactRequest / 100).formatCurrency({
        thousandSeparated: true,
        mantissa: 2,
      });
    },
    createdAt() {
      return moment.utc(this.facebookCampaign.created_time).local();
    },
    createdAtShort() {
      return this.createdAt.format('DD-MM-YYYY');
    },
    createdAtFromNow() {
      return this.createdAt.fromNow();
    },
  },
  methods: {
    showFacebookCampaign() {
      this.$modal.show({
        component: () => import('@/components/FacebookCampaignDialog/FacebookCampaignDialog.vue'),
        props: {
          accountId: this.accountId,
          facebookCampaignId: this.facebookCampaign.id,
          insightsSince: this.$route.params?.since || '',
          insightsUntil: this.$route.params?.until || '',
        },
        on: {},
        hideEvent: 'hide',
      });
    },
  },
};
</script>
